const FRENCH_VOWELS = new Set([
    'a', 'e', 'i', 'o', 'u', 'y',
    'à', 'â', 'ä', 'é', 'è', 'ê', 'ë',
    'ï', 'î', 'ô', 'ù', 'û', 'ü', 'ÿ',
    'œ', 'æ',
]);

const FORBIDDEN_CHARS_REGEX = /[0-9.,;:!?/\\|_@#$%^&*+=~()[\]{}'"""«»<>≤≥±]/;

/**
 * Checks if 4+ consecutive identical letters appear (case-insensitive).
 */
export function hasRepeatedLetters(value: string): boolean {
    const lower = value.toLowerCase();
    let count = 1;
    for (let i = 1; i < lower.length; i++) {
        if (lower[i] === lower[i - 1]) {
            count++;
            if (count >= 4) return true;
        } else {
            count = 1;
        }
    }
    return false;
}

/**
 * Checks if 4+ consecutive consonants appear.
 * Spaces and hyphens reset the count.
 */
export function hasConsonantStreak(value: string): boolean {
    const lower = value.toLowerCase();
    let consonantCount = 0;
    for (const char of lower) {
        if (char === ' ' || char === '-') {
            consonantCount = 0;
            continue;
        }
        if (FRENCH_VOWELS.has(char)) {
            consonantCount = 0;
        } else if (/[a-zA-Zà-ÿœæ]/.test(char)) {
            consonantCount++;
            if (consonantCount >= 4) return true;
        } else {
            consonantCount = 0;
        }
    }
    return false;
}

/**
 * Checks if 3+ hyphens are present in the string.
 */
export function hasTooManyHyphens(value: string): boolean {
    const count = (value.match(/-/g) || []).length;
    return count >= 3;
}

/**
 * Checks if the string contains any forbidden character.
 */
export function hasForbiddenCharacters(value: string): boolean {
    return FORBIDDEN_CHARS_REGEX.test(value);
}

/**
 * Validates a name field (firstname or lastname) against all rules.
 * Returns an error message string or null if valid.
 */
export function validateName(value: string): string | null {
    if (hasForbiddenCharacters(value)) {
        return 'Le champ contient des caractères non autorisés';
    }
    if (hasRepeatedLetters(value)) {
        return 'Le champ contient trop de lettres identiques consécutives';
    }
    if (hasConsonantStreak(value)) {
        return 'Le champ contient trop de consonnes consécutives';
    }
    if (hasTooManyHyphens(value)) {
        return 'Le champ contient trop de tirets';
    }
    return null;
}

/**
 * Checks if firstname and lastname are strictly identical (case-insensitive, trimmed).
 */
export function areNamesIdentical(firstName: string, lastName: string): boolean {
    return firstName.trim().toLowerCase() === lastName.trim().toLowerCase();
}
